import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
    return () => document.body.classList.remove("mobile-menu-open");
  }, [mobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Projects", path: "/projects" },
    { name: "Gallery", path: "/gallery" },
    { name: "Reviews", path: "/reviews" },
    { name: "Finance", path: "/finance" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="header-glass sticky top-0 z-50" data-testid="header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">DC</span>
            </div>
            <span className="font-montserrat font-bold text-xl text-gray-900">
              DC Tiling & Bathrooms
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors ${
                  isActive(item.path)
                    ? "text-primary"
                    : "text-gray-600 hover:text-primary"
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4 ml-6">
            <a
              href="tel:+447547393455"
              className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors whitespace-nowrap"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium">07547 393 455</span>
            </a>
            <Link to="/book">
              <Button 
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10 whitespace-nowrap"
                data-testid="header-book-quote"
              >
                <Calendar className="w-4 h-4 mr-1" />
                Book a Quote
              </Button>
            </Link>
            <Link to="/estimate">
              <Button 
                className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 whitespace-nowrap"
                data-testid="header-cta"
              >
                Start My Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-900" />
            ) : (
              <Menu className="w-6 h-6 text-gray-900" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu — rendered via Portal to escape header's backdrop-filter containing block */}
      {createPortal(
        <div
          className={`mobile-menu ${mobileMenuOpen ? "" : "closed"}`}
          data-testid="mobile-menu"
          aria-hidden={!mobileMenuOpen}
        >
          <div className="container-custom py-6">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">DC</span>
                </div>
                <span className="font-montserrat font-bold text-xl text-gray-900">
                  DC Tiling &amp; Bathrooms
                </span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="p-2"
              >
                <X className="w-6 h-6 text-gray-900" />
              </button>
            </div>

            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-lg font-medium py-3 border-b border-gray-100 ${
                    isActive(item.path) ? "text-primary" : "text-gray-900"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                </Link>
              ))}
              <Link to="/book" onClick={() => setMobileMenuOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary/10 mt-4"
                  data-testid="mobile-book-quote"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Quote
                </Button>
              </Link>
              <Link to="/estimate" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-primary hover:bg-primary/90 text-white mt-2" data-testid="mobile-cta">
                  Start My Estimate
                </Button>
              </Link>
              <a
                href="tel:+447547393455"
                className="flex items-center gap-2 mt-4 text-gray-700 hover:text-primary"
                data-testid="mobile-phone-link"
              >
                <Phone className="w-4 h-4" />
                07547 393 455
              </a>
            </nav>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;
